import {
  LOGO_VIEWBOX,
  LOGO_ARM_GRADIENT,
  LOGO_FOLD_GRADIENT,
  LOGO_ARM_PATH,
  LOGO_ARM_STROKE_WIDTH,
  LOGO_FOLD_PATH,
  LOGO_FOLD_STROKE_WIDTH,
  LOGO_DOT,
  APP_NAME_PARTS,
} from './logoGeometry';

/**
 * Apace Pulse brand mark. The gradient "A" + dot is defined once in
 * logoGeometry.ts (also consumed by scripts/generate-favicon.ts to keep
 * public/favicon.svg in sync) and rendered here as inline SVG — reused for
 * the sidebar header, the login screen, and anywhere else the brand needs
 * to appear. The wordmark is plain HTML/CSS rather than SVG <text> so it
 * inherits the app's font stack and never needs manual width math.
 */
export function LogoMark({ size = 32, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={LOGO_VIEWBOX}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={LOGO_ARM_GRADIENT.id}
          x1={LOGO_ARM_GRADIENT.x1}
          y1={LOGO_ARM_GRADIENT.y1}
          x2={LOGO_ARM_GRADIENT.x2}
          y2={LOGO_ARM_GRADIENT.y2}
        >
          {LOGO_ARM_GRADIENT.stops.map((stop) => (
            <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
          ))}
        </linearGradient>
        <linearGradient
          id={LOGO_FOLD_GRADIENT.id}
          x1={LOGO_FOLD_GRADIENT.x1}
          y1={LOGO_FOLD_GRADIENT.y1}
          x2={LOGO_FOLD_GRADIENT.x2}
          y2={LOGO_FOLD_GRADIENT.y2}
        >
          {LOGO_FOLD_GRADIENT.stops.map((stop) => (
            <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
          ))}
        </linearGradient>
      </defs>
      <path
        d={LOGO_ARM_PATH}
        stroke={`url(#${LOGO_ARM_GRADIENT.id})`}
        strokeWidth={LOGO_ARM_STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={LOGO_FOLD_PATH}
        stroke={`url(#${LOGO_FOLD_GRADIENT.id})`}
        strokeWidth={LOGO_FOLD_STROKE_WIDTH}
        strokeLinecap="round"
      />
      <circle cx={LOGO_DOT.cx} cy={LOGO_DOT.cy} r={LOGO_DOT.r} fill={LOGO_DOT.fill} />
    </svg>
  );
}

export function Logo({
  size = 22,
  className = '',
  wordmarkClassName = '',
}: {
  size?: number;
  className?: string;
  wordmarkClassName?: string;
}) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <LogoMark size={size * 1.4} />
      <span className={`font-extrabold tracking-tight ${wordmarkClassName}`} style={{ fontSize: size }}>
        <span className="text-gray-900">{APP_NAME_PARTS.primary}</span>{' '}
        <span className="bg-gradient-to-r from-violet-600 to-indigo-500 bg-clip-text text-transparent">
          {APP_NAME_PARTS.accent}
        </span>
      </span>
    </div>
  );
}
